import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, ImpersonatorRef } from '@monitor/shared';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  /** Whether the user ticked "remember me" at login. Sessions without it
   *  get auto-logged-out by useIdleLogout after a period of inactivity. */
  rememberMe: boolean;
  /** True when the user logged in with a one-time reset code. While set, the
   *  router redirects every navigation to /first-time-password so the only
   *  thing they can do is set a real password. */
  mustChangePassword: boolean;
  /** When set, the current session is a super_admin impersonating `user`;
   *  this holds the original admin so we can show a banner + return. */
  impersonator: ImpersonatorRef | null;
  setAuth: (data: {
    accessToken: string;
    refreshToken: string;
    user: User;
    rememberMe?: boolean;
    mustChangePassword?: boolean;
    impersonator?: ImpersonatorRef | null;
  }) => void;
  setTokens: (data: {
    accessToken: string;
    refreshToken: string;
    mustChangePassword?: boolean;
  }) => void;
  setUser: (user: User) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      rememberMe: false,
      mustChangePassword: false,
      impersonator: null,
      setAuth: ({
        accessToken,
        refreshToken,
        user,
        rememberMe = false,
        mustChangePassword = false,
        impersonator = null,
      }) => set({ accessToken, refreshToken, user, rememberMe, mustChangePassword, impersonator }),
      setTokens: ({ accessToken, refreshToken, mustChangePassword = false }) =>
        set({ accessToken, refreshToken, mustChangePassword }),
      setUser: (user) => set({ user }),
      clear: () =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          rememberMe: false,
          mustChangePassword: false,
          impersonator: null,
        }),
    }),
    { name: 'auth' },
  ),
);
